// VirtSellOrdersAdmin.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Dropdown, Form, Pagination, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { fetchVirtSellOrdersAdmin } from "../../http/userAPI"; // поправь путь при необходимости

const STATUSES = ["available", "completed", "canceled", "expired"];

const VirtSellOrdersAdmin = () => {
    // ===== Фильтры =====
    const [q, setQ] = useState("");
    const [statuses, setStatuses] = useState([]);            // многозначный фильтр
    const [createdFrom, setCreatedFrom] = useState(null);    // Date | null
    const [createdTo, setCreatedTo] = useState(null);        // Date | null
    const [purchFrom, setPurchFrom] = useState(null);        // Date | null
    const [purchTo, setPurchTo] = useState(null);              // Date | null

    // ===== Пагинация =====
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [count, setCount] = useState(0);
    const totalPages = useMemo(() => Math.max(1, Math.ceil(count / Math.max(1, limit))), [count, limit]);

    // ===== Данные =====
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Скип первого автозапроса (и после Reset)
    const skipNextLoadRef = useRef(true);

    // ===== Хелперы =====
    const toMinutesStartOfDay = (d) => {
        if (!d) return undefined;
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return Math.floor(x.getTime() / 60000);
    };
    const toMinutesEndOfDay = (d) => {
        if (!d) return undefined;
        const x = new Date(d);
        x.setHours(23, 59, 59, 999);
        return Math.floor(x.getTime() / 60000);
    };
    const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");
    const fmtMoney2 = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : "—");

    // Статусы — тумблер
    const toggleStatus = useCallback((s) => {
        setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    }, []);
    const clearStatuses = useCallback(() => setStatuses([]), []);

    // Собираем параметры
    const buildQuery = useCallback(() => {
        const base = {
            page,
            limit,
            q: q?.trim() || undefined,
            statuses: statuses.length ? statuses : undefined,
            created_from: toMinutesStartOfDay(createdFrom),
            created_to: toMinutesEndOfDay(createdTo),
            purchased_from: toMinutesStartOfDay(purchFrom),
            purchased_to: toMinutesEndOfDay(purchTo),
        };
        return Object.fromEntries(Object.entries(base).filter(([, v]) => v !== undefined && v !== "")); // очистим пустые
    }, [page, limit, q, statuses, createdFrom, createdTo, purchFrom, purchTo]);

    // Загрузка
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await fetchVirtSellOrdersAdmin(buildQuery());
            const list = payload?.items || [];
            setItems(list);
            setCount(Number(payload?.count || 0));
            setMessage(list.length ? "" : "No orders found");
        } catch (e) {
            console.error("fetchVirtSellOrdersAdmin failed:", e);
            setItems([]);
            setCount(0);
            setMessage("Failed to fetch orders");
            toast.error("Error fetching orders", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Apply
    const applyFilter = useCallback(async () => {
        setPage(1);
        setLoading(true);
        try {
            const payload = await fetchVirtSellOrdersAdmin({ ...buildQuery(), page: 1 });
            const list = payload?.items || [];
            setItems(list);
            setCount(Number(payload?.count || 0));
            setMessage(list.length ? "" : "No orders found");
        } catch (e) {
            console.error(e);
            toast.error("Failed to apply filter", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Reset — без запроса
    const resetFilters = useCallback(() => {
        skipNextLoadRef.current = true;
        setQ("");
        setStatuses([]);
        setCreatedFrom(null);
        setCreatedTo(null);
        setPurchFrom(null);
        setPurchTo(null);
        setPage(1);
        setItems([]);
        setCount(0);
        setMessage("");
        setLoading(false);
    }, []);

    // Пагинация
    const handlePageChange = useCallback(
        (next) => {
            const p = Math.max(1, Math.min(next, totalPages || 1));
            if (p !== page) setPage(p);
        },
        [page, totalPages]
    );
    const handleLimitChange = useCallback(
        (val) => {
            const l = Math.max(1, Math.min(val || 50, 200));
            if (l !== limit) {
                setLimit(l);
                setPage(1);
            }
        },
        [limit]
    );

    // Автозагрузка при изменении page/limit
    useEffect(() => {
        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            return;
        }
        loadData();
    }, [page, limit, loadData]);

    // ===== РЕНДЕР =====
    return (
        <>
            <Card className="shadow-sm bg-light p-0">
                <Card.Body>
                    {/* Фильтры в одну строку */}
                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-end justify-content-start">
                        {/* Статусы (multi) */}
                        <div>
                            <Dropdown>
                                <Dropdown.Toggle variant="secondary" size="sm" style={{ minWidth: 120 }}>
                                    {statuses.length ? `Selected: ${statuses.length}` : "Status"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ minWidth: 220 }}>
                                    <Dropdown.Item onClick={clearStatuses} active={statuses.length === 0}>
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    {STATUSES.map((s) => (
                                        <Dropdown.Item as="button" key={s} onClick={() => toggleStatus(s)} active={statuses.includes(s)}>
                                            <Form.Check type="checkbox" className="me-2  d-inline-block" checked={statuses.includes(s)} readOnly /> {s}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        {/* Apply / Reset */}
                        <Button variant="success" size="sm" onClick={applyFilter} disabled={loading}>
                            {loading ? (<><Spinner size="sm" className="me-1" />Apply…</>) : "Apply Filter"}
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={resetFilters} disabled={loading}>
                            Reset
                        </Button>

                        {/* Поиск */}
                        <div style={{ width: 200 }} className="align-items-md-start d-none d-md-block">
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Search (user, comment, status)…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                        </div>



                        {/* Date ranges — скрыто на моб, в одну строку на десктопе */}
                        <div className="d-none d-md-flex flex-nowrap align-items-end gap-3">
                            {/* Created range */}
                            <div className="d-flex align-items-end gap-1">
                                <div>
                                    <small className="text-muted d-block mb-1">Created</small>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={createdFrom ? new Date(createdFrom).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setCreatedFrom(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                                <div>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={createdTo ? new Date(createdTo).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setCreatedTo(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                            </div>

                            {/* Purchased range */}
                            <div className="d-flex align-items-end gap-1">
                                <div>
                                    <small className="text-muted d-block mb-1">Purchased</small>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={purchFrom ? new Date(purchFrom).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setPurchFrom(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                                <div>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={purchTo ? new Date(purchTo).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setPurchTo(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                            </div>
                        </div>


                        {/* Per Page (PP) */}
                        <div className="ms-3 d-none d-md-block">
                            <small className="text-muted mb-1 d-block">PP</small>
                            <Form.Select
                                size="sm"
                                value={limit}
                                onChange={(e) => handleLimitChange(Number(e.target.value))}
                                style={{ width: 60 }}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </Form.Select>
                        </div>

                    </div>

                    {/* Таблица */}
                    {message && <p className="text-muted mt-2 text-center">{message}</p>}

                    {loading ? (
                        <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" /> <small>Loading…</small>
                        </div>
                    ) : items.length > 0 ? (
                        <>
                            <div style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 10 }}>
                                <Table striped bordered hover size="sm" className="mb-2 align-middle" style={{ minWidth: 1200 }}>
                                    <thead className="small">
                                    <tr className="small">
                                        <th>ID</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Purchased</th>
                                        <th>SellerID</th>
                                        <th>Seller Name</th>
                                        <th>BuyerID</th>
                                        <th>Buyer Name</th>
                                        <th className="text-end">Virt</th>
                                        <th className="text-end">USD</th>
                                        <th className="text-end">1 USD = V</th>
                                        <th className="text-end">$ / 1000V</th>
                                        <th>Comment</th>
                                        <th>Note</th>
                                    </tr>
                                    </thead>
                                    <tbody className="small">
                                    {items.map((o) => (
                                        <tr key={o.id} className="align-middle small">
                                            <td className="py-0 px-2">{o.id}</td>
                                            <td className="py-0 px-2">
                                                <Badge bg={
                                                    o.order_status === "available" ? "success" :
                                                        o.order_status === "completed" ? "secondary" :
                                                            o.order_status === "canceled" ? "danger" :
                                                                "warning"
                                                }>
                                                    {o.order_status}
                                                </Badge>
                                            </td>
                                            <td className="py-0 px-2">{fmtDateTime(o.created_at_manual)}</td>
                                            <td className="py-0 px-2">{o.purchased_at ? fmtDateTime(o.purchased_at) : "—"}</td>
                                            <td className="py-0 px-2">{o.seller_id}</td>
                                            <td className="py-0 px-2">{o.seller_amz_name || "—"}</td>
                                            <td className="py-0 px-2">{o.buyer_id ?? "—"}</td>
                                            <td className="py-0 px-2">{o.buyer_amz_name || "—"}</td>
                                            <td className="py-0 px-2 text-end">{o.virt_amount}</td>
                                            <td className="py-0 px-2 text-end">${fmtMoney2(o.usd_amount)}</td>
                                            <td className="py-0 px-2 text-end">{fmtMoney2(o.price_virt_per_usd)}</td>
                                            <td className="py-0 px-2 text-end">${fmtMoney2(o.price_usd_per_1000_virt)}</td>
                                            <td className="py-0 px-2">{o.comment || "—"}</td>
                                            <td className="py-0 px-2">{o.internal_note || "—"}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Пагинация снизу */}
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="small text-muted">
                                    Total: {count} • Page {page} of {totalPages}
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page <= 1} />
                                    <Pagination.Item active>{page}</Pagination.Item>
                                    <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} />
                                </Pagination>
                            </div>
                        </>
                    ) : null}
                </Card.Body>
            </Card>
        </>
    );
};

export default VirtSellOrdersAdmin;
